import React, { useMemo, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import {
    AppBar,
    Box,
    Button,
    CircularProgress,
    Container,
    CssBaseline,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Toolbar,
    Typography,
    useMediaQuery
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

// project imports
import useAuth from 'hooks/useAuth';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Customization from '../Customization';
import Footer from './Footer';
import Header from './Header';
import HorizontalBar from './HorizontalBar';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';

// assets
import { IconChevronRight } from '@tabler/icons';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open, layout }) => ({
    ...theme.typography.mainContent,
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: layout === LAYOUT_CONST.VERTICAL_LAYOUT ? -(drawerWidth - 72) : '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            marginTop: layout === 'horizontal' ? 135 : 88
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: 0,
            width: '100%',
            padding: '16px',
            marginTop: 88
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            width: '100%',
            padding: '12px',
            marginRight: 0,
            marginTop: 88
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter
        }),
        marginLeft: layout === 'horizontal' ? '20px' : 0,
        marginTop: layout === 'horizontal' ? 135 : 88,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.up('md')]: {
            marginTop: layout === 'horizontal' ? 135 : 88
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: 0,
            width: '100%',
            marginTop: 88
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            width: '100%',
            marginRight: 0,
            marginTop: 88
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const { user, forceChangePassword, setInitialPin } = useAuth();

    // Password step
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [pwdError, setPwdError] = useState('');
    const [pwdSaving, setPwdSaving] = useState(false);

    // PIN step
    const [showPinStep, setShowPinStep] = useState(false);
    const [newPin, setNewPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [pinError, setPinError] = useState('');
    const [pinSaving, setPinSaving] = useState(false);

    const handleForceChange = async () => {
        if (!newPwd || !confirmPwd) {
            setPwdError('Both fields are required.');
            return;
        }
        if (newPwd.length < 6) {
            setPwdError('Password must be at least 6 characters.');
            return;
        }
        if (newPwd !== confirmPwd) {
            setPwdError('Passwords do not match.');
            return;
        }
        setPwdError('');
        setPwdSaving(true);
        try {
            await forceChangePassword(newPwd, confirmPwd);
            setNewPwd('');
            setConfirmPwd('');
            setShowPinStep(true);
        } catch (err) {
            setPwdError(err.message || 'Failed to change password.');
        } finally {
            setPwdSaving(false);
        }
    };

    const handleSetPin = async () => {
        if (!newPin || !confirmPin) {
            setPinError('Both fields are required.');
            return;
        }
        if (!/^\d{4}$/.test(newPin)) {
            setPinError('PIN must be exactly 4 digits.');
            return;
        }
        if (newPin !== confirmPin) {
            setPinError('PINs do not match.');
            return;
        }
        setPinError('');
        setPinSaving(true);
        try {
            await setInitialPin(newPin, confirmPin);
            setNewPin('');
            setConfirmPin('');
            setShowPinStep(false);
        } catch (err) {
            setPinError(err.message || 'Failed to set PIN.');
        } finally {
            setPinSaving(false);
        }
    };

    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { container, drawerType, layout } = useConfig();

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        } else {
            dispatch(openDrawer(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerType]);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (matchDownMd) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    const condition = layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd;

    const header = useMemo(
        () => (
            <Toolbar sx={{ p: condition ? '10px' : '16px' }}>
                <Header />
            </Toolbar>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [layout, matchDownMd]
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    transition: drawerOpen ? theme.transitions.create('width') : 'none'
                }}
            >
                {header}
            </AppBar>

            {/* horizontal menu-list bar */}
            {layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd && <HorizontalBar />}

            {/* drawer */}
            {(layout === LAYOUT_CONST.VERTICAL_LAYOUT || matchDownMd) && <Sidebar />}

            {/* main content */}
            <Main theme={theme} open={drawerOpen} layout={layout}>
                <Container maxWidth={container ? 'lg' : false} {...(!container && { sx: { px: { xs: 0 } } })}>
                    {/* breadcrumb */}
                    <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                    <Outlet />
                </Container>
                <Footer />
            </Main>
            <Customization />

            {/* Force password change — blocks all access until done */}
            <Dialog
                open={Boolean(user?.mustChangePassword)}
                disableEscapeKeyDown
                PaperProps={{ sx: { borderRadius: '20px', maxWidth: 420, width: '100%' } }}
            >
                <DialogTitle sx={{ pb: 0, pt: 3, px: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg,#7c3aed,#a855f7)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <LockResetIcon sx={{ color: '#fff', fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" fontWeight={800}>
                                Set Your Password
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                You must change your password before continuing
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ px: 3, pt: 2.5, pb: 3 }}>
                    <TextField
                        label="New Password"
                        value={newPwd}
                        onChange={(e) => setNewPwd(e.target.value)}
                        fullWidth
                        size="small"
                        type={showNew ? 'text' : 'password'}
                        autoComplete="new-password"
                        sx={{ mb: 2, mt: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setShowNew((p) => !p)}>
                                        {showNew ? (
                                            <VisibilityOffOutlinedIcon fontSize="small" />
                                        ) : (
                                            <VisibilityOutlinedIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        label="Confirm New Password"
                        value={confirmPwd}
                        onChange={(e) => setConfirmPwd(e.target.value)}
                        fullWidth
                        size="small"
                        type={showConfirm ? 'text' : 'password'}
                        autoComplete="new-password"
                        sx={{ mb: pwdError ? 1 : 2.5, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setShowConfirm((p) => !p)}>
                                        {showConfirm ? (
                                            <VisibilityOffOutlinedIcon fontSize="small" />
                                        ) : (
                                            <VisibilityOutlinedIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    {pwdError && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                            {pwdError}
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        disabled={pwdSaving}
                        onClick={handleForceChange}
                        sx={{ borderRadius: '10px', py: 1.2, fontWeight: 700, fontSize: '0.95rem' }}
                    >
                        {pwdSaving ? <CircularProgress size={20} color="inherit" /> : 'Update Password'}
                    </Button>
                </DialogContent>
            </Dialog>

            {/* Set Security PIN — shown after first password change */}
            <Dialog
                open={showPinStep && !user?.mustChangePassword && !user?.hasPinSet}
                disableEscapeKeyDown
                PaperProps={{ sx: { borderRadius: '20px', maxWidth: 420, width: '100%' } }}
            >
                <DialogTitle sx={{ pb: 0, pt: 3, px: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg,#7c3aed,#a855f7)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <PinOutlinedIcon sx={{ color: '#fff', fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" fontWeight={800}>
                                Set Security PIN
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Set a 4-digit PIN to secure your account
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ px: 3, pt: 2.5, pb: 3 }}>
                    <TextField
                        label="New PIN"
                        value={newPin}
                        onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        fullWidth
                        size="small"
                        type="password"
                        inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                        sx={{ mb: 2, mt: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                    <TextField
                        label="Confirm PIN"
                        value={confirmPin}
                        onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        fullWidth
                        size="small"
                        type="password"
                        inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                        sx={{ mb: pinError ? 1 : 2.5, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                    {pinError && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                            {pinError}
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        disabled={pinSaving}
                        onClick={handleSetPin}
                        sx={{ borderRadius: '10px', py: 1.2, fontWeight: 700, fontSize: '0.95rem' }}
                    >
                        {pinSaving ? <CircularProgress size={20} color="inherit" /> : 'Set PIN'}
                    </Button>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default MainLayout;
